/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *---------------------------------------------------
 */
//standard libraries
import React, { useState, useEffect } from "react";
import { Canceler } from "axios";
import omitBy from "lodash-es/omitBy";
//context
import { useOrdersValue, useProgressValue } from "../context";
//custom libraries

import { useOrderHandler } from "./rest/use-order-handler";
import {
  FORM_FIELD_ORDER_DATE_FROM,
  FORM_FIELD_ORDER_DATE_TO,
  FORM_FIELD_ORDER_ID,
  FORM_FIELD_PHONE,
  LIMIT,
  REG_EX,
  ZERO_OFFSET,
} from "../constants/common";

import {
  ORDERID,
  ORDERTIME,
  LOGINID,
  STATUS,
  SORT_ORDER_NUMBER_ASC,
  SORT_ORDER_NUMBER_DESC,
  SORT_ORDER_DATE_ASC,
  SORT_ORDER_DATE_DESC,
  SORT_LOGONID_ASC,
  SORT_LOGONID_DESC,
  SORT_STATUS_ASC,
  SORT_STATUS_DESC,
} from "../constants/order";
import { useCustomerHandler } from "./rest";
import { useParamsValue } from "../context/search-context";

export interface ParamsJson {
  limit?: number;
  offset?: number;
  [FORM_FIELD_ORDER_ID]?: string;
  findBy?: string;
  logonId?: string;
  logonIdSearchType?: string;
  orderByTableName?: string;
  orderByFieldName?: string;
  firstName?: string;
  firstNameSearchType?: string;
  lastName?: string;
  lastNameSearchType?: string;
  streetAddress1?: string;
  streetAddress1SearchType?: string;
  city?: string;
  citySearchType?: string;
  zipCode?: string;
  zipCodeSearchType?: string;
  country?: string;
  countrySearchType?: string;
  state?: string;
  stateSearchType?: string;
  email?: string;
  emailSearchType?: string;
  [FORM_FIELD_PHONE]?: string;
  phoneSearchType?: string;
  [FORM_FIELD_ORDER_DATE_FROM]?: string;
  [FORM_FIELD_ORDER_DATE_TO]?: string;
  orderDateSearchType?: string;
  addressType?: string;
  parentOrgNameSearchType?: string;
  parentOrgName?: string;
}

export const initParams: ParamsJson = {
  [FORM_FIELD_ORDER_ID]: "",
  logonId: "",
  [FORM_FIELD_ORDER_DATE_FROM]: "",
  [FORM_FIELD_ORDER_DATE_TO]: "",
  firstName: "",
  lastName: "",
  email: "",
  [FORM_FIELD_PHONE]: "",
  streetAddress1: "",
  zipCode: "",
  state: "",
  country: "",
};

export const useOrderForm = () => {
  const [listRecords, setListRecords] = useState<any>([]);
  const { setInProgress } = useProgressValue();
  const { findOrders } = useOrderHandler();
  const { getCustomerByIdRequest } = useCustomerHandler();
  const { orders, setOrders } = useOrdersValue();
  const { params, setParams } = useParamsValue();
  const [chips, setChips] = React.useState<any>({});
  const [showForm, setShowForm] = React.useState<boolean>(true);
  const [formError, setFormError] = React.useState<any>({});
  const [formData, setFormData] = React.useState<ParamsJson | any>(params);
  const [sortOrder, setSortOrder] = useState<string>(() => "asc");
  const [orderBy, setOrderBy] = useState<string>(() => "");
  const [sortParam, setSortParam] = useState<any>();

  const getPrev = () => {
    if (orders.pageNumber > 1) {
      if (sortParam) {
        getOrders(sortParam, (orders.pageNumber - 1) * LIMIT - LIMIT, false);
      } else {
        getOrders(params, (orders.pageNumber - 1) * LIMIT - LIMIT);
      }
    }
  };

  const getNext = () => {
    console.log("next");
    if (sortParam) {
      getOrders(
        sortParam,
        LIMIT + ((orders.pageNumber - 1) * LIMIT + 1),
        false
      );
    } else {
      console.log("next");
      getOrders(params, LIMIT + ((orders.pageNumber - 1) * LIMIT + 1));
    }
  };

  useEffect(() => {
    console.log(formData);
  }, [formData]);

  const submitOrderForm = (evt: any) => {
    setInProgress(true);
    evt.orderDateFrom = `${
      evt.orderDateFrom ? evt.orderDateFrom : "2000-01-01"
    } 00:00:00`;
    evt.orderDateTo = `${
      evt.orderDateTo ? evt.orderDateTo : "2000-01-01"
    } 00:00:00`;
    setParams(evt);
    const chips = omitBy(evt, (s) => String(s).trim() === "");
    console.log(chips);
    const [res, canceler] = findOrders(chips, ZERO_OFFSET, LIMIT);
    cancels.cancels = canceler;

    res
      .then(async (r) => {
        const MAX_PAGE_SIZE = 100;
        let resultList: string | any[] = [];
        let countResults = r.data.recordSetTotal;
        let index = countResults / MAX_PAGE_SIZE;

        if (countResults > 0) {
          for (let i = 0; i < index; i++) {
            const [res, canceler] = findOrders(
              chips,
              i * MAX_PAGE_SIZE,
              MAX_PAGE_SIZE
            );
            cancels.cancels = canceler;
            const data = (await res).data.resultList;
            resultList = [...resultList, ...data];
          }
          setListRecords(resultList);
        } else {
          setListRecords([]);
        }
        /*   const newOrderItems = orders.items.map((o: any) => {
        if (o.orderId === r.data.items[0].orderId) {
          return Object.assign({ ...o }, r.data.items[0]);
        } else {
          return { ...o };
        }
      });
 */
      })
      .catch((e) => console.log(e))
      .finally(() => {
        setInProgress(false);
      });

    getOrders(evt, ZERO_OFFSET);
  };

  const clickSearchSort = () => {
    getOrders(sortParam, ZERO_OFFSET, false);
  };

  const sortAction = (props: string) => {
    if (orderBy === props && sortOrder === "asc") {
      setSortOrder("desc");
    } else if (orderBy === props && sortOrder === "desc") {
      setSortOrder("asc");
    } else if (orderBy !== props && sortOrder === "asc") {
      setSortOrder("desc");
    } else {
      setSortOrder("asc");
    }
    setOrderBy(props);
  };

  const removeChip = (name: string) => {
    const newParams = { ...params, [name]: "" };
    getOrders(newParams, ZERO_OFFSET);
    setParams(newParams);
  };

  const cancels: { cancels: any[] } = { cancels: [] as any[] };

  const getOrder = (id: string) => {
    const [res, canceler] = findOrders({ orderId: id }, ZERO_OFFSET, LIMIT);
    cancels.cancels = canceler;

    res.then((r) => {
      const newOrderItems = orders.items.map((o: any) => {
        if (o.orderId === r.data.items[0].orderId) {
          return Object.assign({ ...o }, r.data.items[0]);
        } else {
          return { ...o };
        }
      });

      setOrders({ ...orders, items: newOrderItems });
    });
  };

  const getOrders = (param: any, newOffset: number, setChip = true) => {
    const chips = omitBy(param, (s) => String(s).trim() === "");
    let searchParams = {};
    if (chips["orderByTableName"] || chips["orderByFieldName"]) {
      const existingParams = omitBy(params, (s) => String(s).trim() === "");
      searchParams = { ...existingParams, ...chips };
    } else searchParams = { ...chips };
    // setInProgress(true);
    const [res, canceler] = findOrders(searchParams, newOffset, LIMIT);
    cancels.cancels = canceler;
    return res
      .then((r) => r.data)
      .then((data) => {
        cancels.cancels = [];
        const pArray: Promise<any>[] = [];
        if (!data.items) {
          data.items = [] as any[];
        }
        const ids: string[] = [];
        for (const o of data.items) {
          // adding buyer info to order
          if (!o.buyer && o.memberId) {
            if (!ids.includes(o.memberId)) {
              ids.push(o.memberId);
              const [res, canceler] = getCustomerByIdRequest(o.memberId);
              cancels.cancels = cancels.cancels.concat(canceler);
              pArray.push(res);
            }
          }
        }
        return Promise.all(pArray).then((values: any[]) => {
          for (const value of values) {
            data.items
              .filter((o: any) => {
                return o.memberId === value.data.id;
              })
              .forEach((o: any) => {
                o.buyer = value.data;
              });
          }
          return data;
        });
      })
      .then((data) => {
        if (setChip) {
          setChips(chips);
          setOrderBy("");
          setSortParam("");
        }

        setOrders(data);
      })
      .catch((e) => console.log(e))
      .finally(() => {
        // setInProgress(false);
      });
  };

  useEffect(() => {
    console.log(params);
    const chips = omitBy(params, (s) => String(s).trim() === "");
    setChips(chips);
    const keys = Object.keys(chips);
    orders.count && keys.length && setShowForm(false);
    return () => {
      cancels.cancels.forEach((cancel: Canceler) => {
        return cancel();
      });
    };
  }, []);

  useEffect(() => {
    if (orderBy) {
      switch (orderBy) {
        case ORDERID:
          setSortParam(
            sortOrder === "asc" ? SORT_ORDER_NUMBER_ASC : SORT_ORDER_NUMBER_DESC
          );
          break;

        case ORDERTIME:
          setSortParam(
            sortOrder === "asc" ? SORT_ORDER_DATE_ASC : SORT_ORDER_DATE_DESC
          );
          break;

        case LOGINID:
          setSortParam(
            sortOrder === "asc" ? SORT_LOGONID_ASC : SORT_LOGONID_DESC
          );
          break;

        case STATUS:
          setSortParam(
            sortOrder === "asc" ? SORT_STATUS_ASC : SORT_STATUS_DESC
          );
          break;

        default:
          setSortParam("");
      }
    }
  }, [orderBy, sortOrder]);

  useEffect(() => {
    if (sortParam) {
      clickSearchSort();
    }
  }, [sortParam]);

  return {
    /*     changeInput, */
    listRecords,
    submitOrderForm,
    orders,
    params,
    formData,
    removeChip,
    chips,
    getOrder,
    getPrev,
    getNext,
    formError,
    showForm,
    setShowForm,
    sortAction,
    sortOrder,
    orderBy,
  };
};
